import { useQuery } from "@tanstack/react-query";
import { Circle, CircleCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import type { MenuSummaryDto } from "../../shared/api-types";
import { api } from "../api/client";
import { useProfile } from "../api/queries";
import { ICON } from "../components/ui";
import { useAllergenDeadlines } from "./nutrition/AllergenBanner";

// Acasă → "Lista de lansare" (owner 2026-11-03, "idiot proof"): everything a restaurant should have done, each with
// its state read from real data and one link that goes straight to it. The QR step cannot be seen by the app, so the
// restaurant ticks it (kept in this browser only).

interface Item {
  id: string;
  done: boolean;
  title: string;
  detail: string;
  to: string;
  action: string;
}

const qrKey = (rid: string) => `meniova:qr-placed:${rid}`;
function readQr(rid: string) {
  try {
    return localStorage.getItem(qrKey(rid)) === "1";
  } catch {
    return false;
  }
}

export type Launch = ReturnType<typeof useLaunch>;

/** The launch steps with their state (Acasă shows the count in its progress ring and the list below). */
export function useLaunch(rid: string, menus: MenuSummaryDto[]) {
  const profile = useProfile(rid);
  const unconfirmed = useAllergenDeadlines(rid);
  const billing = useQuery({
    queryKey: ["billing", rid],
    queryFn: () => api<{ status: string }>("GET", `/restaurants/${rid}/billing`),
    staleTime: 5 * 60_000,
  });
  const [qrPlaced, setQrPlaced] = useState(() => readQr(rid));

  const live = menus.filter((m) => !m.archived && m.publishedVersionId && m.publicVisible);
  const products = live.reduce((n, m) => n + m.productCount, 0);
  const notConfirmed = unconfirmed.data?.length ?? 0;
  const pub = profile.data?.published;
  const sub = billing.data?.status;

  const items: Item[] = [
    {
      id: "publish",
      done: live.length > 0,
      title: "Meniul este publicat",
      detail: live.length ? "Clienții îl văd la link și la codul QR." : "Încă nu e public.",
      to: "/app/products",
      action: "Publică",
    },
    {
      id: "allergens",
      done: live.length > 0 && notConfirmed === 0,
      title: "Alergenii sunt confirmați de tine",
      detail: live.length
        ? notConfirmed
          ? `${products - notConfirmed} din ${products} produse confirmate. Restul arată estimarea Meniova, marcată „neconfirmată”.`
          : "Toate produsele publice au alergenii confirmați."
        : "După publicare, confirmi alergenii fiecărui produs.",
      to: "/app/nutrition/alergeni",
      action: "Confirmă alergenii",
    },
    {
      id: "frozen",
      done: live.length > 0 && live.every((m) => m.frozenChecked),
      title: "Produsele congelate sunt marcate",
      detail: "Pornești „Din produs congelat” la fiecare preparat făcut din produse congelate.",
      to: "/app/products",
      action: "Verifică produsele",
    },
    {
      id: "hours",
      done: !!pub?.hours,
      title: "Programul de funcționare",
      detail: "Apare la finalul meniului, pe zile.",
      to: "/app/settings",
      action: "Completează",
    },
    {
      id: "contact",
      done: !!(pub?.phone || pub?.address),
      title: "Telefon și adresă",
      detail: "Clienții te pot suna sau găsi direct din meniu.",
      to: "/app/settings",
      action: "Completează",
    },
    {
      id: "plan",
      done: sub === "active" || sub === "beta",
      title: "Abonamentul ales",
      detail:
        sub === "trialing"
          ? "Ești în perioada gratuită. Alege planul ca meniul să rămână online după probă."
          : sub === "active" || sub === "beta"
            ? "Meniul rămâne online."
            : "Fără abonament activ, meniul se închide pentru clienți.",
      to: "/app/billing",
      action: "Alege planul",
    },
    {
      id: "qr",
      done: qrPlaced,
      title: "Codurile QR sunt pe mese",
      detail: "Descarcă codul sau suportul de masă A6 și pune-le la vedere.",
      to: "/app/qr",
      action: "Descarcă QR",
    },
  ];
  const doneCount = items.filter((i) => i.done).length;
  function toggleQr() {
    const next = !qrPlaced;
    setQrPlaced(next);
    try {
      localStorage.setItem(qrKey(rid), next ? "1" : "0");
    } catch {
      // Private window: the tick lasts for this visit only.
    }
  }
  return { items, doneCount, qrPlaced, toggleQr, ready: !profile.isPending && !unconfirmed.isPending };
}

export function LaunchChecklist({ launch }: { launch: Launch }) {
  const { items, doneCount, qrPlaced, toggleQr, ready } = launch;
  const [open, setOpen] = useState(doneCount < items.length);
  if (!ready) return null;

  return (
    <section className="card stack launch" aria-labelledby="ov-launch" style={{ gap: 12 }}>
      <div className="row" style={{ gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
        <div className="stack" style={{ gap: 4 }}>
          <h2 id="ov-launch" className="h2" style={{ margin: 0 }}>
            Lista de lansare
          </h2>
          <span className="page-sub">
            {doneCount === items.length
              ? "Totul e gata. Meniul tău e complet."
              : `${doneCount} din ${items.length} pași gata`}
          </span>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen((o) => !o)}>
          {open ? "Ascunde" : "Arată pașii"}
        </button>
      </div>
      <div
        className="launch-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={items.length}
        aria-valuenow={doneCount}
        aria-label="Progres lansare"
      >
        <span className="launch-fill" style={{ width: `${(doneCount / items.length) * 100}%` }} />
      </div>
      {open && (
        <ul className="launch-list">
          {items.map((i) => (
            <li key={i.id} className={i.done ? "done" : undefined}>
              {i.done ? (
                <CircleCheck {...ICON} color="var(--color-success)" aria-label="Gata" />
              ) : (
                <Circle {...ICON} color="var(--color-text-muted)" aria-label="De făcut" />
              )}
              <div className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                <strong style={{ fontWeight: 600 }}>{i.title}</strong>
                <span className="caption muted">{i.detail}</span>
              </div>
              {i.id === "qr" ? (
                <span className="row" style={{ gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {!i.done && (
                    <Link className="btn btn-secondary btn-sm" to={i.to}>
                      {i.action}
                    </Link>
                  )}
                  <button type="button" className="btn btn-ghost btn-sm" onClick={toggleQr}>
                    {qrPlaced ? "Anulează bifa" : "Am pus codurile"}
                  </button>
                </span>
              ) : (
                !i.done && (
                  <Link className="btn btn-secondary btn-sm" to={i.to}>
                    {i.action}
                  </Link>
                )
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
